import React, {useState, useEffect, memo, useMemo} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';

import {useApolloClient, useQuery} from '@apollo/client/react';
import {StatusBadge} from '../components/StatusBadge';
import {AuthTypeBadge} from '../components/AuthTypeBadge';
import {Avatar} from '../components/Avatar';
import {DateWithRelative} from '../components/DateWithRelative';
import {SplitUserModal} from '../components/SplitUserModal';
import {isFreeEmail} from '../constants/freeEmailDomains';
import {isMultipleEmailsInOne} from '../utils/emailUtils';
import {AlertTriangle, Download, Split} from 'lucide-react';
import {gql} from 'types/__generated__';
import type {FindUsersInAnyOrganisationQuery} from 'types/__generated__/graphql';
import {
  CREATED_PARAM,
  CREATED_PRESETS,
  FLAG_FILTERS,
  LOGIN_PARAM,
  LOGIN_PRESETS,
  ORG_PARAM,
  SORT_PARAM,
  SORT_PRESETS,
  clearedFilterParams,
  countActiveFilters,
  filterFromParams,
  sortFromParams,
  startOfHour,
} from '../utils/userListFilters';

export const FIND_USERS_QUERY = gql(`
  query FindUsersInAnyOrganisation($username: String, $paging: Paging, $filter: UserFilter, $sort: UserSort) {
    findUsersInAnyOrganisation(username: $username, paging: $paging, filter: $filter, sort: $sort) {
      users {
        id
        username
        email
        firstName
        lastName
        name
        enabled
        acceptedTerms
        isSSO
        earlyAccessProgram
        createdAt
        lastLoginAnyOrganisation
        organisationMemberships {
          name
          organisationId
        }
        workInformation {
          jobTitle
          companyName
          department
        }
      }
      page
      totalHits
    }
  }
`);

export const EXPORT_USERS_QUERY = gql(`
  query ExportUsersInAnyOrganisation($username: String, $filter: UserFilter, $sort: UserSort) {
    exportUsersInAnyOrganisation(username: $username, filter: $filter, sort: $sort)
  }
`);

function downloadCsv(csv: string, fileName: string) {
  const url = URL.createObjectURL(new Blob([csv], {type: 'text/csv;charset=utf-8'}));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  // Revoking in the same tick can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

const selectClass =
  'px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent';

interface FilterSelectProps {
  label: string;
  value: string;
  options: {value: string; label: string}[];
  onChange: (value: string | null) => void;
  anyLabel?: string;
}

function FilterSelect({label, value, options, onChange, anyLabel = 'Any'}: FilterSelectProps) {
  const id = `user-filter-${label.toLowerCase().replace(/\W+/g, '-')}`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-gray-500 dark:text-gray-400">
        {label}
      </label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value || null)} className={selectClass}>
        <option value="">{anyLabel}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

const YES_NO = [
  {value: 'yes', label: 'Yes'},
  {value: 'no', label: 'No'},
];

const presetOptions = (presets: Record<string, {label: string}>) => Object.entries(presets).map(([value, {label}]) => ({value, label}));

/** A user as FIND_USERS_QUERY selects it. */
type UserListRow = FindUsersInAnyOrganisationQuery['findUsersInAnyOrganisation']['users'][number];

// Memoized UserRow component to prevent unnecessary re-renders
interface UserRowProps {
  user: UserListRow;
  onNavigate: (username: string) => void;
  onNavigateToOrg: (orgId: string) => void;
  onSplitUser: (user: UserListRow) => void;
}

const VISIBLE_ORG_COUNT = 3;

const UserRow = memo(({user, onNavigate, onNavigateToOrg, onSplitUser}: UserRowProps) => {
  const isRogueUser = !user.organisationMemberships || user.organisationMemberships.length === 0;
  const hasDangerousEmail = isFreeEmail(user.email);
  const isMalformedEmail = isMultipleEmailsInOne(user.email);
  const [showAllOrgs, setShowAllOrgs] = useState(false);

  const organisations = user.organisationMemberships || [];
  const hiddenOrgCount = Math.max(0, organisations.length - VISIBLE_ORG_COUNT);
  const visibleOrgs = showAllOrgs ? organisations : organisations.slice(0, VISIBLE_ORG_COUNT);

  return (
    <tr className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${isRogueUser ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''}`}>
      <td className="px-2 py-4" style={{width: '40px'}}>
        {isRogueUser && (
          <span className="text-yellow-500" title="Warning: User not assigned to any organization">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        )}
      </td>
      <td className="px-6 py-4" style={{minWidth: '200px', maxWidth: '300px'}}>
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Avatar firstName={user.firstName} lastName={user.lastName} size="sm" />
          </div>
          <div className="ml-4 min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{user.name}</div>
            <div className="flex items-center gap-1">
              <a
                href={`mailto:${user.email}?subject=${encodeURIComponent('Regarding your account in Cway')}&body=${encodeURIComponent('I am contacting you regarding your account in Cway.')}`}
                onClick={(e) => e.stopPropagation()}
                className={`text-sm truncate block ${
                  isMalformedEmail
                    ? 'text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-semibold'
                    : hasDangerousEmail
                      ? 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300'
                      : 'text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
                }`}
              >
                {user.email}
              </a>
              {isMalformedEmail && (
                <span title="Malformed email - contains multiple comma-separated emails" className="flex-shrink-0">
                  <Split size={14} className="text-orange-600 dark:text-orange-400" aria-label="Malformed email" />
                </span>
              )}
              {hasDangerousEmail && !isMalformedEmail && (
                <span title="Free email provider - potentially dangerous for business use" className="flex-shrink-0">
                  <AlertTriangle size={14} className="text-red-600 dark:text-red-400" aria-label="Free email provider" />
                </span>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <StatusBadge type="user" enabled={user.enabled} acceptedTerms={user.acceptedTerms} />
      </td>
      <td className="px-6 py-4" style={{maxWidth: '250px'}}>
        {isRogueUser ? (
          <span className="text-yellow-600 dark:text-yellow-400 font-semibold text-sm">No Organization</span>
        ) : (
          <div className="flex flex-wrap items-center gap-1">
            {visibleOrgs.map((org, index) => (
              <button
                key={org.organisationId || index}
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigateToOrg(org.organisationId);
                }}
                title={org.name}
                className="max-w-full truncate rounded-full bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 text-xs text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
              >
                {org.name}
              </button>
            ))}
            {hiddenOrgCount > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllOrgs((prev) => !prev);
                }}
                className="rounded-full px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {showAllOrgs ? 'Show less' : `+${hiddenOrgCount} more`}
              </button>
            )}
          </div>
        )}
        {user.workInformation?.jobTitle && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">{user.workInformation.jobTitle}</div>
        )}
        {user.workInformation?.department && (
          <div className="text-xs text-gray-500 dark:text-gray-400 break-words">{user.workInformation.department}</div>
        )}
        {user.workInformation?.companyName && (
          <div className="text-xs text-gray-500 dark:text-gray-400 break-words">{user.workInformation.companyName}</div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`text-sm ${isRogueUser ? 'text-yellow-600 dark:text-yellow-400 font-semibold' : 'text-gray-600 dark:text-gray-400'}`}>
          {user.organisationMemberships?.length || 0}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <AuthTypeBadge isSSO={user.isSSO} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <DateWithRelative timestamp={user.createdAt} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {user.lastLoginAnyOrganisation ? (
          <DateWithRelative timestamp={user.lastLoginAnyOrganisation} />
        ) : (
          <span className="text-sm text-gray-400 dark:text-gray-500">Never</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end space-x-2">
          {isMalformedEmail && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSplitUser(user);
              }}
              className="p-2 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors"
              title="Split malformed user account"
            >
              <Split className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={() => onNavigate(user.username)}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="View details"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </td>
    </tr>
  );
});

UserRow.displayName = 'UserRow';

const PAGE_SIZE = 50;

export default function UsersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const searchTerm = searchParams.get('q') || '';
  const currentPage = parseInt(searchParams.get('page') || '0', 10) || 0;
  const paramsKey = searchParams.toString();
  // Relative presets ("last 30 days") are anchored when the URL changes, not on every render.
  const {filter, sort} = useMemo(() => {
    const params = new URLSearchParams(paramsKey);
    return {filter: filterFromParams(params, startOfHour(Date.now())), sort: sortFromParams(params)};
  }, [paramsKey]);
  const activeFilterCount = countActiveFilters(searchParams);

  const [searchInput, setSearchInput] = useState(searchTerm);
  const [splitModalUser, setSplitModalUser] = useState<UserListRow | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const client = useApolloClient();

  const {loading, error, data} = useQuery(FIND_USERS_QUERY, {
    variables: {
      username: searchTerm.trim() || undefined,
      paging: {page: currentPage, pageSize: PAGE_SIZE},
      filter,
      sort,
    },
    fetchPolicy: 'cache-and-network',
  });

  const users = data?.findUsersInAnyOrganisation?.users || [];
  const totalHits = data?.findUsersInAnyOrganisation?.totalHits || 0;

  // Debounce search input → URL param
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== searchTerm) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          if (trimmed === '') {
            next.delete('q');
          } else {
            next.set('q', trimmed);
          }
          next.delete('page');
          return next;
        });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, searchTerm, setSearchParams]);

  // Keep local input in sync with URL (for back navigation)
  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  const totalPages = Math.ceil(totalHits / PAGE_SIZE);

  const updateParams = (updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      return next;
    });
  };

  // Any filter or sort change starts over from the first page.
  const setFilterParam = (key: string, value: string | null) => updateParams({[key]: value, page: null});

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const result = await client.query({
        query: EXPORT_USERS_QUERY,
        variables: {username: searchTerm.trim() || undefined, filter, sort},
        fetchPolicy: 'network-only',
      });
      const csv = result.data?.exportUsersInAnyOrganisation;
      if (csv === undefined) throw new Error('The server returned no export');
      downloadCsv(csv, `cway-users-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  };

  const orgParam = searchParams.get(ORG_PARAM) || '';
  const orgOptions = [
    {value: 'some', label: 'Has organisation'},
    {value: 'none', label: 'No organisation'},
    // The organisation page links here with its id.
    ...(filter?.organisationId ? [{value: orgParam, label: 'This organisation'}] : []),
  ];

  const handleNavigate = React.useCallback(
    (username: string) => {
      navigate(`/admin/users/${username}`);
    },
    [navigate],
  );

  const handleNavigateToOrg = React.useCallback(
    (orgId: string) => {
      navigate(`/admin/organisations/${orgId}`);
    },
    [navigate],
  );

  const handleSplitUser = React.useCallback((user: UserListRow) => {
    setSplitModalUser(user);
  }, []);

  const handleSplitSuccess = () => {
    window.location.reload();
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <h3 className="text-red-800 dark:text-red-200 font-semibold">Error loading users</h3>
          <p className="text-red-600 dark:text-red-300 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Users</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Manage user accounts and permissions</p>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700 relative">
        {/* Loading indicator */}
        {loading && <div className="absolute top-0 left-0 right-0 h-1 bg-blue-600 animate-pulse z-10" />}

        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-end gap-3">
            {FLAG_FILTERS.map(({param, label}) => (
              <FilterSelect
                key={param}
                label={label}
                value={searchParams.get(param) || ''}
                options={YES_NO}
                onChange={(value) => setFilterParam(param, value)}
              />
            ))}
            <FilterSelect label="Organisation" value={orgParam} options={orgOptions} onChange={(value) => setFilterParam(ORG_PARAM, value)} />
            <FilterSelect
              label="Last login"
              value={searchParams.get(LOGIN_PARAM) || ''}
              options={presetOptions(LOGIN_PRESETS)}
              onChange={(value) => setFilterParam(LOGIN_PARAM, value)}
            />
            <FilterSelect
              label="Created"
              value={searchParams.get(CREATED_PARAM) || ''}
              options={presetOptions(CREATED_PRESETS)}
              anyLabel="Any time"
              onChange={(value) => setFilterParam(CREATED_PARAM, value)}
            />
            <FilterSelect
              label="Sort"
              value={searchParams.get(SORT_PARAM) || ''}
              options={presetOptions(SORT_PRESETS)}
              anyLabel="Default"
              onChange={(value) => setFilterParam(SORT_PARAM, value)}
            />
            <div className="ml-auto flex items-center gap-2">
              {activeFilterCount > 0 && (
                <button
                  onClick={() => setSearchParams((prev) => clearedFilterParams(prev))}
                  className="px-3 py-1.5 text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                >
                  Clear filters ({activeFilterCount})
                </button>
              )}
              <button
                onClick={handleExport}
                disabled={exporting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
              >
                <Download size={14} aria-hidden="true" />
                {exporting ? 'Exporting…' : 'Export CSV'}
              </button>
            </div>
          </div>
          {exportError && <p className="mt-2 text-sm text-red-600 dark:text-red-400">Export failed: {exportError}</p>}
        </div>

        {/* Search Bar */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchInput !== searchTerm && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-2 py-3 text-center" style={{width: '40px'}}>
                  {/* Warning icon column */}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Organisation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"># Orgs</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Auth Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last login</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((user) => (
                <UserRow key={user.id} user={user} onNavigate={handleNavigate} onNavigateToOrg={handleNavigateToOrg} onSplitUser={handleSplitUser} />
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-500">No users found matching your criteria</p>
          </div>
        )}
      </div>

      {/* Pagination and Results count */}
      <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing {users.length} of {totalHits} users
          {totalPages > 1 && ` - Page ${currentPage + 1} of ${totalPages}`}
        </div>

        {totalPages > 1 && (
          <div className="flex gap-2">
            <button
              onClick={() => updateParams({page: currentPage - 1 === 0 ? null : String(currentPage - 1)})}
              disabled={currentPage === 0}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <div className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage + 1} of {totalPages}
            </div>
            <button
              onClick={() => updateParams({page: String(currentPage + 1)})}
              disabled={currentPage >= totalPages - 1}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Split User Modal */}
      {splitModalUser && (
        <SplitUserModal user={splitModalUser} isOpen={!!splitModalUser} onClose={() => setSplitModalUser(null)} onSuccess={handleSplitSuccess} />
      )}
    </div>
  );
}
